import React, { createContext, useContext, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";

import URL_PATTERNS from "../urls";
import { isLoggedIn } from "./auth";

/* Per-tab history memory for the Capacitor native build. Tracks the most
 * recent path the user visited within each tab so that tapping a tab again
 * can restore the user to where they left off, mimicking React Navigation's
 * per-tab back stacks.
 *
 * This is the lightweight Phase-C-Option-1 implementation: the global
 * react-router history is unchanged. The native tab bar consults the
 * provider on tap to decide where to send the user. */

export type TabKey = "home" | "rewards" | "wines" | "taste" | "more";

interface TabConfig {
  /** Substrings that mean "the current URL belongs to this tab." Longest match wins. */
  matches: string[];
  /** Where to navigate if the tab has no remembered URL yet. */
  defaultTo: () => string;
}

export const TAB_CONFIG: Record<TabKey, TabConfig> = {
  home: {
    /* URL_PATTERNS.HOME is "/au/" — a prefix that matches every URL on
     * the site, which would let the home tab swallow pages owned by no
     * other tab (e.g. /au/my-next-wines/). Only match the dashboard
     * exactly; the LOGIN / HOME landing pages aren't routine
     * destinations once the user is past NativeEntryRedirect. */
    matches: [URL_PATTERNS.MY_DASHBOARD],
    defaultTo: () => (isLoggedIn() ? URL_PATTERNS.MY_DASHBOARD : URL_PATTERNS.HOME),
  },
  rewards: {
    matches: ["/rewards"],
    defaultTo: () => URL_PATTERNS.REWARDS_STORE,
  },
  wines: {
    matches: ["/wines", "/wine-lists", "/collections", "/special-packs"],
    defaultTo: () => URL_PATTERNS.ALL_WINES,
  },
  taste: {
    matches: ["/my-taste-profile"],
    defaultTo: () => URL_PATTERNS.MY_TASTE_PROFILE,
  },
  more: {
    matches: ["/my-account", "/my-orders", "/my-cellar", "/my-badges", "/my-progress", "/contact", "/gifts"],
    defaultTo: () => URL_PATTERNS.MY_ACCOUNT,
  },
};

const TAB_KEYS = Object.keys(TAB_CONFIG) as TabKey[];

export const owningTab = (pathname: string): TabKey | null =>
  TAB_KEYS.reduce<{ key: TabKey; len: number } | null>((best, key) => {
    const longest = TAB_CONFIG[key].matches
      .filter((p) => pathname.startsWith(p) || pathname.includes(p))
      .sort((a, b) => b.length - a.length)[0];
    if (longest && (!best || longest.length > best.len)) {
      return { key, len: longest.length };
    }
    return best;
  }, null)?.key ?? null;

interface TabState {
  lastUrl: { [K in TabKey]?: string };
  lastScrollY: { [K in TabKey]?: number };
}

interface TabHistoryContextValue {
  /** Returns the URL to navigate to for the given tab — saved last URL or default. */
  resolveTabUrl: (tab: TabKey) => string;
  /** Returns saved scrollY for a tab, or 0. */
  resolveTabScroll: (tab: TabKey) => number;
  /** Save the current page's scroll position under the tab that owns the current URL. */
  recordCurrentScroll: () => void;
}

const TabHistoryContext = createContext<TabHistoryContextValue | null>(null);

interface ProviderProps {
  children: React.ReactNode;
}

export const NativeTabHistoryProvider = ({ children }: ProviderProps) => {
  const location = useLocation();
  const stateRef = useRef<TabState>({ lastUrl: {}, lastScrollY: {} });

  // Whenever the URL changes to a tab-owned page, remember it under that tab.
  useEffect(() => {
    const tab = owningTab(location.pathname);
    if (!tab) return;
    stateRef.current.lastUrl[tab] = `${location.pathname}${location.search}${location.hash}`;
  }, [location.pathname, location.search, location.hash]);

  const value = useMemo<TabHistoryContextValue>(
    () => ({
      resolveTabUrl: (tab) => stateRef.current.lastUrl[tab] ?? TAB_CONFIG[tab].defaultTo(),
      resolveTabScroll: (tab) => stateRef.current.lastScrollY[tab] ?? 0,
      recordCurrentScroll: () => {
        const tab = owningTab(window.location.pathname);
        if (!tab) return;
        stateRef.current.lastScrollY[tab] = window.scrollY;
      },
    }),
    []
  );

  return <TabHistoryContext.Provider value={value}>{children}</TabHistoryContext.Provider>;
};

export const useNativeTabHistory = (): TabHistoryContextValue => {
  const ctx = useContext(TabHistoryContext);
  if (!ctx) {
    // The provider may be absent during SSR / prerender / tests. Fall back
    // to defaults so the tab bar still navigates to something sensible.
    return {
      resolveTabUrl: (tab) => TAB_CONFIG[tab].defaultTo(),
      resolveTabScroll: () => 0,
      recordCurrentScroll: () => undefined,
    };
  }
  return ctx;
};
